import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PhoneIcon from '@mui/icons-material/Phone';
import { motion, AnimatePresence } from 'framer-motion';

const pages = [
  { title: 'Home', path: '/' },
  { title: 'About', path: '/about' },
  { title: 'Services', path: '/services' },
  { title: 'Fleet', path: '/fleet' },
  { title: 'Contact', path: '/contact' },
];

const settings = [
  { title: 'Dashboard', path: '/dashboard' },
  { title: 'Profile', path: '/dashboard/profile' },
  { title: 'Logout', path: '/login' },
];

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if user is authenticated (in a real app, this would be more complex)
  const isAuthenticated = false;

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Top Bar with Contact Info */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          py: 0.8,
          position: 'relative',
          zIndex: 1100,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                24/7 Support: +91 6351003667
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <AppBar 
        position="sticky" 
        sx={{ 
          background: isScrolled 
            ? 'rgba(255, 255, 255, 0.95)' 
            : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: isScrolled 
            ? '0 4px 20px rgba(0, 0, 0, 0.08)' 
            : '0 2px 10px rgba(0, 0, 0, 0.03)',
          color: theme.palette.primary.main,
          transition: 'all 0.3s ease',
          py: isScrolled ? 0.5 : 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo - Desktop */}
            <Box 
              sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                alignItems: 'center',
                mr: 4
              }}
              component={motion.div}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.secondary.main, 0.15),
                  p: 1,
                  borderRadius: '50%',
                  mr: 1.5
                }}
              >
                <DirectionsCarIcon 
                  sx={{ 
                    fontSize: '2rem',
                    color: theme.palette.secondary.main
                  }} 
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  noWrap
                  component={RouterLink}
                  to="/"
                  sx={{
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 700,
                    letterSpacing: '.1rem',
                    color: 'inherit',
                    textDecoration: 'none',
                    fontSize: '1.5rem',
                    lineHeight: 1.2,
                  }}
                >
                  THE UNITED TRAVEL
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    letterSpacing: '.15rem',
                    color: theme.palette.text.secondary,
                    display: 'block',
                    ml: 0.2,
                    fontWeight: 500,
                  }}
                >
                  STANDARD TO LUXURY TRANSPORTATION
                </Typography>
              </Box>
            </Box>

            {/* Mobile menu button */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMobileMenuToggle}
                color="inherit"
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 1.5,
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Logo - Mobile */}
            <Box 
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.secondary.main, 0.15),
                  p: 0.7,
                  borderRadius: '50%',
                  mr: 1
                }}
              >
                <DirectionsCarIcon 
                  sx={{ 
                    fontSize: '1.6rem',
                    color: theme.palette.secondary.main
                  }} 
                />
              </Box>
              <Typography
                variant="h6"
                noWrap
                component={RouterLink}
                to="/"
                sx={{
                  fontFamily: 'Playfair Display, serif',
                  fontWeight: 700,
                  letterSpacing: '.1rem',
                  color: 'inherit',
                  textDecoration: 'none',
                  fontSize: '1.2rem',
                }}
              >
                THE UNITED TRAVEL
              </Typography>
            </Box>

            {/* Desktop Menu */}
            <Box 
              sx={{ 
                flexGrow: 1, 
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center'
              }}
            >
              {pages.map((page, index) => (
                <motion.div
                  key={page.title}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <Button
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    sx={{
                      my: 2,
                      mx: 1.5,
                      color: 'inherit',
                      display: 'block',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '8px',
                        left: '50%',
                        width: location.pathname === page.path ? '40%' : '0%',
                        height: '2px',
                        backgroundColor: theme.palette.secondary.main,
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&:hover::after': {
                        width: '40%',
                      },
                    }}
                  >
                    {page.title}
                  </Button>
                </motion.div>
              ))}
            </Box>

            {/* Book Now Button and User Menu */}
            <Box 
              sx={{ 
                flexGrow: 0, 
                display: 'flex', 
                alignItems: 'center' 
              }}
              component={motion.div}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              style={{ display: 'none' }}
            >
              <Button
                component={RouterLink}
                to="/booking"
                variant="contained"
                color="secondary"
                sx={{ 
                  display: { xs: 'none', md: 'flex' },
                  mr: 2,
                  px: 3,
                  py: 1.2,
                  fontWeight: 600,
                  borderRadius: 30,
                  boxShadow: '0 4px 14px rgba(201, 153, 92, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(201, 153, 92, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Book Now
              </Button>
              
              {isAuthenticated ? (
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar 
                      alt="User Avatar" 
                      src="/static/images/avatar/1.jpg"
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        border: `2px solid ${theme.palette.secondary.main}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  color="primary"
                  sx={{ 
                    display: { xs: 'none', md: 'flex' },
                    fontWeight: 600,
                    borderWidth: '2px',
                    borderRadius: 30,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      borderWidth: '2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  Login
                </Button>
              )}
              
              <Menu
                sx={{ 
                  mt: '45px',
                  '& .MuiPaper-root': {
                    borderRadius: 2,
                    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                  },
                }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem 
                    key={setting.title} 
                    onClick={handleCloseUserMenu}
                    component={RouterLink}
                    to={setting.path}
                    sx={{
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <Typography textAlign="center" fontWeight={500}>{setting.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile menu drawer */}
      <Drawer
        anchor="left"
        open={isMobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: '75%',
            maxWidth: '300px',
            boxShadow: '0 0 50px rgba(0,0,0,0.2)',
          },
        }}
      >
        <Box
          sx={{
            width: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.light, 0.05)}, white 80%)`,
          }}
          role="presentation"
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              pb: 2,
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                p: 1,
                borderRadius: '50%',
                mr: 1.5
              }}
            >
              <DirectionsCarIcon 
                sx={{ 
                  fontSize: '1.8rem',
                  color: theme.palette.secondary.main
                }} 
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Playfair Display, serif',
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  lineHeight: 1.2,
                }}
              >
                THE UNITED TRAVEL
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  letterSpacing: '.1rem',
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                STANDARD TO LUXURY TRANSPORTATION
              </Typography>
            </Box>
          </Box>
          
          <List sx={{ flexGrow: 1 }}>
            {pages.map((page) => (
              <ListItem 
                key={page.title} 
                disablePadding 
                sx={{ mb: 1 }}
                onClick={handleMobileMenuToggle}
              >
                <ListItemButton 
                  component={RouterLink} 
                  to={page.path}
                  selected={location.pathname === page.path}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    borderLeft: location.pathname === page.path ? 
                      `4px solid ${theme.palette.secondary.main}` : 'none',
                    pl: location.pathname === page.path ? 2 : 3,
                    bgcolor: location.pathname === page.path ? 
                      alpha(theme.palette.secondary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemText 
                    primary={page.title} 
                    primaryTypographyProps={{
                      fontWeight: location.pathname === page.path ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
            <Button
              component={RouterLink}
              to="/booking"
              variant="contained"
              color="secondary"
              fullWidth
              sx={{ 
                py: 1.5,
                fontWeight: 600,
                borderRadius: 30,
                boxShadow: '0 4px 14px rgba(201, 153, 92, 0.3)',
              }}
              onClick={handleMobileMenuToggle}
            >
              Book Now
            </Button>
            
            {!isAuthenticated && (
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                color="primary"
                fullWidth
                sx={{ 
                  py: 1.5,
                  fontWeight: 600,
                  borderWidth: '2px',
                  borderRadius: 30,
                }}
                onClick={handleMobileMenuToggle}
              >
                Login
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Header; 